import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AddOccupantFormProps {
  isOpen: boolean;
  onClose: () => void;
  onAddOccupant: (registrationNumber: string) => Promise<void>;
  dormName: string;
}

const AddOccupantForm = ({ isOpen, onClose, onAddOccupant, dormName }: AddOccupantFormProps) => {
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedRegNumber = registrationNumber.trim();
    if (!trimmedRegNumber) {
      toast({
        title: "Invalid Input",
        description: "Student registration number cannot be empty.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onAddOccupant(trimmedRegNumber);
      toast({
        title: "Student Added Successfully",
        description: `Student ${trimmedRegNumber} has been added to ${dormName}.`,
        duration: 3000,
      });
      setRegistrationNumber("");
      onClose();
    } catch (error: any) {
      console.error("Error adding occupant:", error);
      toast({
        title: "Error Adding Student",
        description: `Failed to add student: ${error.message || "An unexpected error occurred."}`,
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
            <UserPlus className="h-5 w-5" />
            Add Student to {dormName}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="regNumber" className="text-sm font-medium text-gray-900 dark:text-white">
              Student Registration Number
            </Label>
            <Input
              id="regNumber"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              placeholder="Enter student registration number"
              className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-700"
              disabled={isSubmitting}
            >
              <UserPlus className="h-4 w-4" />
              Add Student
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddOccupantForm;